import { ColumnsType } from "antd/es/table";
import {
  useTimeSlotSessionItineraryItemsQuery,
  useTimeSlotSessionSeminarSessionsQuery,
} from "api/queries/timeslotSession";
import { Drawer, Table, Tabs } from "components/styleguide";
import {
  TimeSlotSessionApiForumsForumIdTimeslotsTimeslotIdSessionsSessionIdItineraryItemsGetRequest,
  TimeSlotSessionApiForumsForumIdTimeslotsTimeslotIdSessionsSessionIdSeminarSessionsGetRequest,
  TimeSlotSessionResponseModel,
  TimeSlotType,
} from "generated/api";
import { toNumber } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { getColumnSearchProps, ResetFunction, SearchFunctionType } from "utils/tableColumnUtils";

type SessionDependencyDrawerProps = {
  open: boolean;
  timeslotId: number;
  timeSlotType?: TimeSlotType;
  session?: TimeSlotSessionResponseModel;
  onClose: () => void;
};

type ItineraryItemRow = {
  id: number;
  attendeeId: number;
  firstName?: string;
  lastName?: string;
  peopleCode?: string;
};

type SeminarSessionRow = {
  id: number;
  seminarCode: string;
  seminarTitle?: string;
  facility?: string;
};

const DEFAULT_PAGE_SIZE = 10;
const ITINERARY_TAB = "itinerary";
const SEMINAR_TAB = "seminars";
const sortDirections: Record<string, string> = {
  ascend: "asc",
  descend: "desc",
};
type PageResponse = { data?: { items?: unknown[]; total?: number } };

function getItems<T>(response?: PageResponse) {
  return (response?.data?.items ?? []) as T[];
}
const getTotal = (response?: PageResponse) => response?.data?.total ?? 0;

const SessionDependencyDrawer = ({
  open,
  timeslotId,
  timeSlotType,
  session,
  onClose,
}: SessionDependencyDrawerProps) => {
  const { t } = useTranslation();
  const { id, lang } = useParams();
  const forumId = toNumber(id);
  const sessionId = toNumber(session?.id);
  const isConference = timeSlotType === TimeSlotType.Conference;
  const [activeTab, setActiveTab] = useState(ITINERARY_TAB);
  const [itineraryPage, setItineraryPage] = useState(1);
  const [seminarPage, setSeminarPage] = useState(1);
  const [itinerarySearch, setItinerarySearch] = useState<Record<string, string | undefined>>({});
  const [seminarSearch, setSeminarSearch] = useState<Record<string, string | undefined>>({});
  const [itinerarySort, setItinerarySort] = useState<Record<string, string | undefined>>({});
  const [seminarSort, setSeminarSort] = useState<Record<string, string | undefined>>({});
  const itineraryItemsCount = session?.dependencies?.itineraryItemsCount ?? 0;
  const seminarSessionsCount = isConference ? session?.dependencies?.seminarSessionsCount ?? 0 : 0;
  const hasItineraryItems = itineraryItemsCount > 0;
  const hasSeminarSessions = seminarSessionsCount > 0;
  const itineraryItemsRequest: TimeSlotSessionApiForumsForumIdTimeslotsTimeslotIdSessionsSessionIdItineraryItemsGetRequest =
    {
      forumId,
      timeslotId,
      sessionId,
      pageNumber: itineraryPage,
      pageSize: DEFAULT_PAGE_SIZE,
      ...itinerarySearch,
      ...itinerarySort,
    };

  const itineraryItemsQuery = useTimeSlotSessionItineraryItemsQuery(itineraryItemsRequest, {
    enabled: open && !!sessionId,
  });

  const seminarSessionsRequest: TimeSlotSessionApiForumsForumIdTimeslotsTimeslotIdSessionsSessionIdSeminarSessionsGetRequest =
    {
      forumId,
      timeslotId,
      sessionId,
      pageNumber: seminarPage,
      pageSize: DEFAULT_PAGE_SIZE,
      ...seminarSearch,
      ...seminarSort,
    };

  const seminarSessionsQuery = useTimeSlotSessionSeminarSessionsQuery(seminarSessionsRequest, {
    enabled: open && !!sessionId && isConference,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    if (hasItineraryItems) {
      setActiveTab(ITINERARY_TAB);
      return;
    }

    if (isConference && hasSeminarSessions) {
      setActiveTab(SEMINAR_TAB);
      return;
    }

    setActiveTab(ITINERARY_TAB);
  }, [hasItineraryItems, hasSeminarSessions, isConference, open, sessionId]);

  const handleTabChange = (key: string) => {
    if (key === ITINERARY_TAB && hasItineraryItems) {
      setActiveTab(key);
    }

    if (key === SEMINAR_TAB && isConference && hasSeminarSessions) {
      setActiveTab(key);
    }
  };

  const attendeeUrl = (attendeeId: number) => `/${lang}/forum/${forumId}/attendees/${attendeeId}#itinerary`;
  const seminarUrl = (seminarCode: string) =>
    `/${lang}/forum/${forumId}/conference/seminars?code=${encodeURIComponent(seminarCode)}`;

  const handleItinerarySearch: SearchFunctionType = (selectedKeys, confirm, _dataIndex, searchSet) => {
    setItinerarySearch((current) => ({
      ...current,
      [searchSet]: selectedKeys[0] || undefined,
    }));
    setItineraryPage(1);
    confirm?.();
  };

  const handleItineraryReset: ResetFunction = (clearFilters, _dataIndex, searchSet, confirm) => {
    setItinerarySearch((current) => ({
      ...current,
      [searchSet]: undefined,
    }));
    setItineraryPage(1);
    clearFilters?.();
    confirm?.();
  };

  const handleSeminarSearch: SearchFunctionType = (selectedKeys, confirm, _dataIndex, searchSet) => {
    setSeminarSearch((current) => ({
      ...current,
      [searchSet]: selectedKeys[0] || undefined,
    }));
    setSeminarPage(1);
    confirm?.();
  };

  const handleSeminarReset: ResetFunction = (clearFilters, _dataIndex, searchSet, confirm) => {
    setSeminarSearch((current) => ({
      ...current,
      [searchSet]: undefined,
    }));
    setSeminarPage(1);
    clearFilters?.();
    confirm?.();
  };

  const handleItineraryTableChange = (_: unknown, __: unknown, sorter: any) => {
    const tableSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    setItinerarySort({
      orderBy: tableSorter?.order ? tableSorter.field : undefined,
      orderDir: tableSorter?.order ? sortDirections[tableSorter.order] : undefined,
    });
    setItineraryPage(1);
  };

  const handleSeminarTableChange = (_: unknown, __: unknown, sorter: any) => {
    const tableSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    setSeminarSort({
      orderBy: tableSorter?.order ? tableSorter.field : undefined,
      orderDir: tableSorter?.order ? sortDirections[tableSorter.order] : undefined,
    });
    setSeminarPage(1);
  };

  const itineraryColumns: ColumnsType<ItineraryItemRow> = [
    {
      title: t("Attendee Name"),
      dataIndex: "firstName",
      key: "attendeeName",
      sorter: true,
      render: (_: unknown, record) => (
        <Link to={attendeeUrl(record.attendeeId)} target="_blank">
          {[record.firstName, record.lastName].filter(Boolean).join(" ")}
        </Link>
      ),
      ...getColumnSearchProps<ItineraryItemRow>("firstName", handleItinerarySearch, handleItineraryReset, "firstName"),
    },
    {
      title: t("peopleType"),
      dataIndex: "peopleCode",
      key: "peopleCode",
      sorter: true,
      ...getColumnSearchProps<ItineraryItemRow>(
        "peopleCode",
        handleItinerarySearch,
        handleItineraryReset,
        "peopleCode",
      ),
    },
    {
      title: t("People Code"),
      dataIndex: "attendeeId",
      key: "attendeeId",
      sorter: true,
      ...getColumnSearchProps<ItineraryItemRow>(
        "attendeeId",
        handleItinerarySearch,
        handleItineraryReset,
        "attendeeId",
      ),
    },
  ];

  const seminarColumns: ColumnsType<SeminarSessionRow> = [
    {
      title: t("Seminar Code"),
      dataIndex: "seminarCode",
      key: "seminarCode",
      sorter: true,
      render: (value: string) => (
        <Link target="_blank" to={seminarUrl(value)}>
          {value}
        </Link>
      ),
      ...getColumnSearchProps<SeminarSessionRow>("seminarCode", handleSeminarSearch, handleSeminarReset, "seminarCode"),
    },
    {
      title: t("Seminar Title"),
      dataIndex: "seminarTitle",
      key: "seminarTitle",
      sorter: true,
      ...getColumnSearchProps<SeminarSessionRow>(
        "seminarTitle",
        handleSeminarSearch,
        handleSeminarReset,
        "seminarTitle",
      ),
    },
    {
      title: t("Facility"),
      dataIndex: "facility",
      key: "facility",
      sorter: true,
      ...getColumnSearchProps<SeminarSessionRow>("facility", handleSeminarSearch, handleSeminarReset, "facility"),
    },
  ];

  return (
    <Drawer title={t("Session dependencies")} open={open} onClose={onClose} width={760} destroyOnClose>
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <Tabs.TabPane tab={t("Itinerary Items")} key={ITINERARY_TAB} disabled={!hasItineraryItems}>
          <Table
            rowKey="id"
            loading={itineraryItemsQuery.isLoading}
            dataSource={getItems(itineraryItemsQuery.data) as ItineraryItemRow[]}
            pagination={{
              current: itineraryPage,
              pageSize: DEFAULT_PAGE_SIZE,
              total: getTotal(itineraryItemsQuery.data),
              showSizeChanger: false,
              onChange: setItineraryPage,
            }}
            columns={itineraryColumns}
            onChange={handleItineraryTableChange}
          />
        </Tabs.TabPane>
        {isConference && (
          <Tabs.TabPane tab={t("Seminar Sessions")} key={SEMINAR_TAB} disabled={!hasSeminarSessions}>
            <Table
              rowKey="id"
              loading={seminarSessionsQuery.isLoading}
              dataSource={getItems(seminarSessionsQuery.data) as SeminarSessionRow[]}
              pagination={{
                current: seminarPage,
                pageSize: DEFAULT_PAGE_SIZE,
                total: getTotal(seminarSessionsQuery.data),
                showSizeChanger: false,
                onChange: setSeminarPage,
              }}
              columns={seminarColumns}
              onChange={handleSeminarTableChange}
            />
          </Tabs.TabPane>
        )}
      </Tabs>
    </Drawer>
  );
};

export default SessionDependencyDrawer;
